import { ADMIN_PERMISSION, type roles } from "@shared/permissions";
import { authClient } from "./auth-client";

// Helpers for the Admin page (user management, #206). The page itself calls
// Better Auth's admin plugin directly via `authClient.admin.*` (deleting a
// user also settles their shared account server-side, via a database hook in
// worker/index.ts).

/**
 * Roles an admin can assign from the Admin page, lowest to highest. They form a
 * ladder (see shared/permissions.ts), so a user is given exactly one.
 */
export const ASSIGNABLE_ROLES = ["user", "debug", "admin"] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export const ROLE_LABELS: Record<AssignableRole, string> = {
  user: "User",
  debug: "Debug",
  admin: "Admin",
};

function isAssignableRole(role: string): role is AssignableRole {
  return (ASSIGNABLE_ROLES as readonly string[]).includes(role);
}

/**
 * The role names in a raw `user.role` value. Better Auth stores it as a
 * comma-separated list, and NULL/empty means its default "user".
 */
export function parseRoles(role: string | null | undefined): string[] {
  const names = (role ?? "")
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
  return names.length > 0 ? names : ["user"];
}

/**
 * The user's role when it's exactly one assignable role, or null when it's a
 * combination or something the picker doesn't know (set directly in D1).
 */
export function singleAssignableRole(
  role: string | null | undefined,
): AssignableRole | null {
  const names = parseRoles(role);
  return names.length === 1 && isAssignableRole(names[0]) ? names[0] : null;
}

/** Display label for one role name (unknown names are shown as-is). */
export function roleLabel(name: string): string {
  return isAssignableRole(name) ? ROLE_LABELS[name] : name;
}

/** Whether a user with this role may use the Admin page (#206). */
export function canAccessAdmin(role: string | null | undefined): boolean {
  return authClient.admin.checkRolePermission({
    // Typed as a known role name, but it's a raw DB value: Better Auth handles
    // comma-separated lists and unknown roles (no permissions) at runtime.
    role: (role ?? "user") as keyof typeof roles,
    permissions: ADMIN_PERMISSION,
  });
}

/** Whether `role` (a raw, possibly comma-separated DB value) includes admin. */
export function isAdminRole(role: string | null | undefined): boolean {
  return parseRoles(role).includes("admin");
}
